// ── JobSpecDriftBadge — Layer 3e UI surface ──
// Read-only integrity signal. Renders only when getJobSpecDrift() reports
// hasDrift === true. No gates, no mutations, no dismiss action.
//
// Visibility: shown to all viewers of Job Detail. Drift is execution
// integrity, not financial data. If a permission key is later introduced
// for execution-integrity signals, gate here in one place.

import { AlertTriangle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAppData } from "@/data/AppDataContext";
import { useInstallerExecution } from "@/data/InstallerExecutionContext";
import { getJobSpecDrift } from "@/data/jobSpecJobDrift";
import type { Job } from "@/data/types";

interface Props {
  job: Job;
}

export function JobSpecDriftBadge({ job }: Props) {
  const { specExecutions } = useAppData();
  const { proofGapEvents } = useInstallerExecution();

  const report = getJobSpecDrift({ job, specExecutions, proofGapEvents });
  if (!report.hasDrift) return null;

  const phaseCount = report.affectedPhases.length;
  const submittedList = report.submittedUnderVersions.join(", ");
  const currentLabel =
    report.currentBoundVersion !== undefined
      ? `v${report.currentBoundVersion}`
      : "unbound";

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            variant="outline"
            className="border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300 gap-1.5 cursor-help"
          >
            <AlertTriangle className="h-3 w-3" />
            <span>
              Spec drift · {phaseCount} {phaseCount === 1 ? "phase" : "phases"}
            </span>
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs">
          <div className="space-y-1.5 text-xs">
            <p className="font-semibold">Job spec version drift detected</p>
            <p className="text-muted-foreground">
              Work submitted under v{submittedList} · currently bound to{" "}
              {currentLabel}.
            </p>
            {phaseCount > 0 && (
              <p className="text-muted-foreground">
                Affected: {report.affectedPhases.join(", ")}
              </p>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
